// Shared license-number sanity check used across Agent/Broker/Brokerage
// verification and TC registration — a candidate under this length is
// rejected before it's ever sent to the verify-license API.
export const MIN_LICENSE_NUMBER_LENGTH = 8;

export const isValidLicenseNumber = (value) => (value || '').trim().length >= MIN_LICENSE_NUMBER_LENGTH;

// ── Texas (TREC) ───────────────────────────────────────────────────────────
// TREC numbers are 4-8 digits plus a license-type suffix: 859229-SA (sales
// agent), 736484-B (individual broker), 9017353-BB (broker company). The inputs
// strip non-alphanumerics as the user types, so "859229-SA" arrives here as
// "859229SA"; the backend restores the hyphen. The suffix is optional because
// the backend resolves a bare number by role. A broker number such as "736484B"
// is only 7 characters, so the shared 8-character floor above must not apply.
export const TX_LICENSE_PATTERN = /^\d{4,8}(-?(SA|BB|B))?$/i;

// State-aware format check for pages that have no per-state rule table.
// Never stricter than the shared check: anything isValidLicenseNumber accepts is
// still accepted for every state, and Texas additionally accepts the TREC
// shapes (4-8 digits + optional suffix) that the 8-character floor rejects.
export const isValidLicenseNumberForState = (value, state) => {
  if (isValidLicenseNumber(value)) return true;
  return (state || '').toUpperCase() === 'TX' && TX_LICENSE_PATTERN.test((value || '').trim());
};

// Affiliation licenseTypes the Texas registry returns for a sponsor.
export const TX_FIRM_LICENSE_TYPES = ['Broker Company', 'Broker Individual'];
export const TX_BROKER_LICENSE_TYPE = 'Broker Individual';

// Display name of an affiliation that stands for a person (broker of record).
// CA/CO affiliations carry firstName/lastName and are unchanged. A Texas
// sponsor's nested entry only has fullName, so that is used as a fallback for
// Broker Individual entries (and only for those).
export const affiliationPersonName = (a) => {
  if (!a) return '';
  const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  return a.licenseType === TX_BROKER_LICENSE_TYPE ? (a.fullName || '').trim() : '';
};

// The number to save on the profile. For Texas the registry's canonical form
// ("859174-SA") is preferred over what was typed ("859174SA" / bare "859174"),
// so the stored number matches the registry. Other states: what was typed.
export const canonicalLicenseNumber = (typed, response, state) => {
  const t = (typed || '').trim();
  if ((state || '').toUpperCase() === 'TX') {
    const canonical = typeof response?.licenseNumber === 'string' ? response.licenseNumber.trim() : '';
    if (canonical) return canonical;
  }
  return t;
};
